import React from 'react';
import { motion } from 'framer-motion';
import { Sparkles, Zap, Brain } from 'lucide-react';
import AIVideoHeader from '../AIVideoHeader';
import AIVideoFooter from '../AIVideoFooter';
import VideoBackground from '../VideoBackground';
import techFlowBg from '@/assets/tech-flow-bg.jpg';

interface Step5AIPromiseProps {
  onNext: () => void;
  onBack: () => void;
}

const Step5AIPromise: React.FC<Step5AIPromiseProps> = ({ onNext, onBack }) => {
  const fallbackBackground = (
    <div className="absolute inset-0">
      <div className="absolute inset-0 bg-gradient-to-br from-black via-gray-900/30 to-black"></div>
      <motion.div 
        className="absolute top-1/4 left-1/4 w-24 h-24 bg-gradient-to-r from-gray-700 to-gray-500 rounded-full opacity-20 blur-lg"
        animate={{ 
          rotateX: 360,
          rotateY: 360,
          z: [0, 50, 0]
        }}
        transition={{ 
          duration: 8, 
          repeat: Infinity,
          ease: "linear"
        }}
        style={{ transformStyle: "preserve-3d" }}
      />
    </div>
  );

  return (
    <div className="min-h-screen bg-black relative overflow-hidden flex flex-col">
      <VideoBackground
        videoSrc="https://res.cloudinary.com/drinkamrs/video/upload/v1752589518/Characters_the_trainee_202507140455_sf20iu.mp4"
        fallbackComponent={fallbackBackground}
        overlay={true}
      />
      <AIVideoHeader 
        currentStep={5} 
        totalSteps={8} 
        onBack={onBack} 
      />

      <div className="relative z-10 flex-1 px-6 py-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="max-w-md mx-auto text-center"
        >
          {/* Visual */}
          <motion.div 
            className="relative mb-8"
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            <div className="relative w-40 h-40 mx-auto">
              <motion.div
                className="absolute inset-0 bg-gradient-to-r from-gray-600 to-gray-400 rounded-full opacity-20"
                animate={{ scale: [1, 1.1, 1] }}
                transition={{ duration: 3, repeat: Infinity }}
              />
              <div className="absolute inset-6 bg-gradient-to-r from-gray-700 to-gray-500 rounded-full flex items-center justify-center">
                <Brain className="w-16 h-16 text-white" />
              </div>
              <motion.div
                className="absolute -top-2 -right-2"
                animate={{ 
                  rotate: 360,
                  scale: [1, 1.2, 1] 
                }}
                transition={{ 
                  rotate: { duration: 4, repeat: Infinity, ease: "linear" },
                  scale: { duration: 2, repeat: Infinity }
                }}
              >
                <Sparkles className="w-8 h-8 text-yellow-500" />
              </motion.div>
              <motion.div
                className="absolute -bottom-2 -left-2"
                animate={{ 
                  y: [-5, 5, -5],
                  scale: [1, 1.1, 1]
                }}
                transition={{ 
                  y: { duration: 2, repeat: Infinity },
                  scale: { duration: 2, repeat: Infinity, delay: 1 }
                }}
              >
                <Zap className="w-8 h-8 text-orange-500" />
              </motion.div>
            </div>
          </motion.div>

          {/* Headline */}
          <motion.h2 
            className="text-3xl font-bold text-white mb-6 leading-tight"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
          >
            <span className="font-extrabold bg-gradient-to-r from-gray-300 to-white bg-clip-text text-transparent">Effortless Creation:</span> AI{' '}
            <span className="font-extrabold bg-gradient-to-r from-gray-200 to-gray-100 bg-clip-text text-transparent">Transforms</span> Your Video Vision into Reality
          </motion.h2>

          {/* Body Text */}
          <motion.p 
            className="text-lg text-gray-300 mb-8 leading-relaxed"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.6 }}
          >
            Our course empowers you to bypass traditional hurdles. AI becomes your <span className="text-gray-300 font-semibold">personal production studio</span>, handling <span className="text-white font-semibold">scripting, voiceovers, visuals</span>, and even <span className="text-gray-200 font-semibold">character generation</span>. Focus on your story, and let AI do the heavy lifting.
          </motion.p>

          {/* Features Grid */}
          <motion.div 
            className="grid grid-cols-2 gap-4 mb-8"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.8 }}
          >
            <motion.div 
              className="bg-white/10 backdrop-blur-sm p-4 rounded-xl border border-gray-500/30 hover:border-gray-400/50 transition-all duration-300 group"
              whileHover={{ scale: 1.05, rotateY: 5 }}
              style={{ transformStyle: "preserve-3d" }}
            >
              <motion.div 
                className="text-2xl mb-2"
                animate={{ rotate: [0, 10, -10, 0] }}
                transition={{ duration: 2, repeat: Infinity, repeatDelay: 3 }}
              >
                ✍️
              </motion.div>
              <div className="text-sm font-semibold text-white group-hover:text-gray-300">AI Scripting</div>
            </motion.div>
            <motion.div 
              className="bg-white/10 backdrop-blur-sm p-4 rounded-xl border border-gray-500/30 hover:border-gray-400/50 transition-all duration-300 group"
              whileHover={{ scale: 1.05, rotateY: -5 }}
              style={{ transformStyle: "preserve-3d" }}
            >
              <motion.div 
                className="text-2xl mb-2"
                animate={{ scale: [1, 1.1, 1] }}
                transition={{ duration: 1.5, repeat: Infinity, repeatDelay: 2 }}
              >
                🎙️
              </motion.div>
              <div className="text-sm font-semibold text-white group-hover:text-gray-300">Voice Generation</div>
            </motion.div>
            <motion.div 
              className="bg-white/10 backdrop-blur-sm p-4 rounded-xl border border-gray-500/30 hover:border-gray-400/50 transition-all duration-300 group"
              whileHover={{ scale: 1.05, rotateX: 5 }}
              style={{ transformStyle: "preserve-3d" }}
            >
              <motion.div 
                className="text-2xl mb-2"
                animate={{ rotateZ: [0, 15, -15, 0] }}
                transition={{ duration: 2.5, repeat: Infinity, repeatDelay: 1 }}
              >
                🎨
              </motion.div>
              <div className="text-sm font-semibold text-white group-hover:text-gray-300">Visual Creation</div>
            </motion.div>
            <motion.div 
              className="bg-white/10 backdrop-blur-sm p-4 rounded-xl border border-gray-500/30 hover:border-gray-400/50 transition-all duration-300 group"
              whileHover={{ scale: 1.05, rotateX: -5 }}
              style={{ transformStyle: "preserve-3d" }}
            >
              <motion.div 
                className="text-2xl mb-2"
                animate={{ y: [0, -5, 0] }}
                transition={{ duration: 1.8, repeat: Infinity, repeatDelay: 1.5 }}
              >
                👥
              </motion.div>
              <div className="text-sm font-semibold text-white group-hover:text-gray-300">Character AI</div>
            </motion.div>
          </motion.div>

          {/* CTA Button */}
          <motion.button
            onClick={onNext}
            className="bg-gradient-to-r from-gray-800 to-black text-white px-8 py-4 rounded-full font-semibold text-lg shadow-lg hover:shadow-xl transition-all duration-300"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 1 }}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            Continue
          </motion.button>
        </motion.div>
      </div>

      <AIVideoFooter />
    </div>
  );
};

export default Step5AIPromise;